import React, { useState } from "react";
import { C, RFLogo } from "../theme";
import { fmt, fmtD, Tag, Kpi, Btn, Sel, stars } from "../shared";
import { IMMEUBLES, chartCharges } from "../data";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export const ResidentPortal = ({ resident, cotisations, charges, travaux, factures, onLogout }) => {
  const [tab, setTab] = useState("apercu");
  const imm = IMMEUBLES.find(i => i.id === resident.immId) || IMMEUBLES[0];
  const myCots = cotisations.filter(c => c.residentId === resident.id).sort((a,b) => new Date(b.echeance) - new Date(a.echeance));
  const myFacts = factures.filter(f => f.residentId === resident.id);
  const totalPaye = myCots.filter(c => c.statut === "payé").reduce((a,c) => a + c.montant, 0);
  const totalDu = myCots.filter(c => c.statut !== "payé").reduce((a,c) => a + c.montant, 0);
  const enRetard = myCots.filter(c => c.statut === "retard" || c.statut === "impayé");
  const myShare = resident.tantiemes / imm.total;

  const TABS = [
    {k:"apercu",i:"📊",l:"Mon aperçu"},
    {k:"cotisations",i:"💰",l:"Cotisations"},
    {k:"factures",i:"🧾",l:"Factures"},
    {k:"charges",i:"🏢",l:"Charges"},
    {k:"travaux",i:"🔧",l:"Travaux"},
    {k:"contact",i:"✉️",l:"Contact"},
  ];

  return (
    <div style={{minHeight:"100vh",background:C.bg}}>
      <div style={{background:C.navy,height:62,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 34px",boxShadow:"0 2px 20px rgba(11,19,51,.22)",position:"sticky",top:0,zIndex:50}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <RFLogo size={34} />
          <div>
            <p style={{fontFamily:"Syne",fontWeight:800,color:"#fff",fontSize:15,lineHeight:1}}>RésiFlow</p>
            <p style={{color:"rgba(255,255,255,.35)",fontSize:11}}>Portail Copropriétaire · {imm.nom}</p>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{textAlign:"right"}}>
            <p style={{color:"#fff",fontWeight:600,fontSize:13}}>{resident.prenom} {resident.nom}</p>
            <p style={{color:"rgba(255,255,255,.35)",fontSize:11}}>Apt. {resident.apt}</p>
          </div>
          <button className="btn" onClick={onLogout}
            style={{padding:"6px 14px",borderRadius:9,border:"1px solid rgba(255,255,255,.2)",background:"transparent",color:"rgba(255,255,255,.55)",fontSize:13,fontWeight:600}}>
            Déconnexion
          </button>
        </div>
      </div>

      {resident.solde < 0 && (
        <div style={{background:"linear-gradient(90deg,#b91c1c,#dc2626)",padding:"10px 34px",display:"flex",alignItems:"center",gap:10}}>
          <span>⚠️</span>
          <p style={{color:"#fff",fontWeight:600,fontSize:14}}>Solde débiteur de <strong>{fmt(Math.abs(resident.solde))}</strong>. Merci de régulariser votre situation.</p>
        </div>
      )}

      <div style={{padding:"26px 34px"}}>
        <div style={{display:"flex",gap:4,marginBottom:24,background:C.card,padding:5,borderRadius:13,width:"fit-content",boxShadow:"0 2px 10px rgba(11,19,51,.05)"}}>
          {TABS.map(t => (
            <button key={t.k} className="btn" onClick={() => setTab(t.k)}
              style={{padding:"7px 14px",borderRadius:9,fontSize:13,fontWeight:600,
                background:tab===t.k?C.teal:"transparent",color:tab===t.k?"#fff":C.muted,
                display:"inline-flex",alignItems:"center",gap:5}}>
              {t.i} {t.l}
            </button>
          ))}
        </div>

        {tab === "apercu" && (
          <div className="fi">
            <div className="fu" style={{background:`linear-gradient(135deg,${C.navy},#1a2b7a)`,borderRadius:18,padding:"26px 30px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:"0 8px 36px rgba(11,19,51,.2)"}}>
              <div>
                <p style={{color:"rgba(255,255,255,.4)",fontSize:11,fontWeight:700,letterSpacing:".8px",textTransform:"uppercase",marginBottom:7}}>Copropriétaire · {imm.nom}</p>
                <h2 style={{fontSize:28,fontWeight:800,color:"#fff",letterSpacing:"-0.5px",marginBottom:4}}>{resident.prenom} {resident.nom}</h2>
                <p style={{color:"rgba(255,255,255,.5)",fontSize:14}}>Apt. <strong style={{color:"#fff"}}>{resident.apt}</strong> · <strong style={{color:"#fff"}}>{resident.tantiemes}</strong> tantièmes / {imm.total}</p>
              </div>
              <div style={{textAlign:"right"}}>
                <p style={{color:"rgba(255,255,255,.4)",fontSize:11,fontWeight:700,textTransform:"uppercase",marginBottom:6}}>Solde actuel</p>
                <p style={{fontSize:40,fontWeight:800,fontFamily:"Syne",color:resident.solde<0?"#ff9090":resident.solde>0?"#6effc8":"#fff",lineHeight:1}}>{fmt(resident.solde)}</p>
                <div style={{marginTop:9}}><Tag s={resident.statut} /></div>
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
              <Kpi icon="✅" label="Total payé" value={fmt(totalPaye)} color={C.green} delay={0} />
              <Kpi icon="⏳" label="Restant dû" value={fmt(totalDu)} sub={`${enRetard.length} en retard`} color={enRetard.length > 0 ? C.red : C.muted} delay={60} />
              <Kpi icon="📋" label="Appels de fonds" value={myCots.length} color={C.blue} delay={120} />
            </div>

            <div className="fu" style={{animationDelay:"150ms",background:C.card,borderRadius:16,padding:22,boxShadow:"0 2px 14px rgba(11,19,51,.06)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                <h4 style={{fontSize:15,fontWeight:700,color:C.text}}>📅 Historique de paiements</h4>
                <Btn onClick={() => alert("📄 Relevé PDF généré !")} small>📥 Relevé PDF</Btn>
              </div>
              <div style={{position:"relative",paddingLeft:28}}>
                <div style={{position:"absolute",left:9,top:8,bottom:8,width:2,background:C.border,borderRadius:2}} />
                {myCots.map((c, i) => {
                  const dc = {payé:C.green,retard:C.red,impayé:C.red,"en attente":C.gold}[c.statut] || C.muted;
                  return (
                    <div key={c.id} style={{position:"relative",marginBottom:i < myCots.length-1 ? 14 : 0}}>
                      <div style={{position:"absolute",left:-21,top:13,width:13,height:13,borderRadius:"50%",background:dc,border:"3px solid #fff",boxShadow:`0 0 0 3px ${dc}33`}} />
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 14px",borderRadius:11,background:C.bg,border:`1px solid ${C.border}`}}>
                        <div>
                          <p style={{fontWeight:600,color:C.text,fontSize:14}}>{c.type}</p>
                          <p style={{fontSize:12,color:C.muted,marginTop:2}}>
                            Échéance : {fmtD(c.echeance)}
                            {c.paiement && <span style={{color:C.green}}> · Payé le {fmtD(c.paiement)}</span>}
                          </p>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <p style={{fontWeight:700,fontSize:15,color:C.text}}>{fmt(c.montant)}</p>
                          <Tag s={c.statut} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "cotisations" && (
          <div className="fi">
            <div style={{background:C.card,borderRadius:16,boxShadow:"0 2px 14px rgba(11,19,51,.06)",overflow:"hidden"}}>
              <div style={{padding:"17px 22px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <h4 style={{fontSize:15,fontWeight:700,color:C.text}}>Mes cotisations</h4>
                <Btn onClick={() => alert("📄 Relevé généré !")} small>📥 PDF</Btn>
              </div>
              <table>
                <thead><tr><th>Type</th><th>Montant</th><th>Échéance</th><th>Paiement</th><th>Statut</th></tr></thead>
                <tbody>
                  {myCots.map(c => (
                    <tr key={c.id}>
                      <td style={{fontWeight:600}}>{c.type}</td>
                      <td style={{fontWeight:700}}>{fmt(c.montant)}</td>
                      <td style={{color:C.muted}}>{fmtD(c.echeance)}</td>
                      <td style={{color:c.paiement?C.green:C.muted}}>{fmtD(c.paiement)}</td>
                      <td><Tag s={c.statut} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {enRetard.length > 0 && (
              <div style={{background:"#fff9f9",border:`1.5px solid ${C.red}44`,borderRadius:13,padding:20,marginTop:16}}>
                <h4 style={{fontSize:14,fontWeight:700,color:C.red,marginBottom:12}}>⚠️ {enRetard.length} paiement(s) en retard</h4>
                {enRetard.map(c => (
                  <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.red}22`}}>
                    <div>
                      <p style={{fontWeight:600,color:C.text}}>{c.type}</p>
                      <p style={{fontSize:12,color:C.muted}}>{fmtD(c.echeance)}</p>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <p style={{fontWeight:800,color:C.red,fontSize:17}}>{fmt(c.montant)}</p>
                      <Tag s={c.statut} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "factures" && (
          <div className="fi">
            <div style={{background:C.card,borderRadius:16,boxShadow:"0 2px 14px rgba(11,19,51,.06)",overflow:"hidden"}}>
              <div style={{padding:"17px 22px",borderBottom:`1px solid ${C.border}`}}>
                <h4 style={{fontSize:15,fontWeight:700,color:C.text}}>Mes factures</h4>
              </div>
              <table>
                <thead><tr><th>N° Facture</th><th>Description</th><th>Émission</th><th>Échéance</th><th>Montant</th><th>Statut</th><th></th></tr></thead>
                <tbody>
                  {myFacts.length > 0 ? myFacts.map(f => (
                    <tr key={f.id}>
                      <td style={{fontWeight:700,color:C.blue}}>FAC-2024-{String(f.id).padStart(3,"0")}</td>
                      <td style={{fontWeight:600}}>{f.type}</td>
                      <td style={{color:C.muted}}>{fmtD(f.date)}</td>
                      <td style={{color:C.muted}}>{fmtD(f.echeance)}</td>
                      <td style={{fontWeight:700}}>{fmt(f.montant)}</td>
                      <td><Tag s={f.statut} /></td>
                      <td>
                        <button className="btn" onClick={() => alert("📄 Facture téléchargée !")}
                          style={{padding:"5px 11px",background:C.blueL,color:C.blue,borderRadius:8,fontWeight:600,fontSize:12}}>
                          📥 PDF
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} style={{textAlign:"center",color:C.muted,padding:30}}>Aucune facture</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "charges" && (
          <div className="fi">
            <div style={{background:C.card,borderRadius:16,padding:22,boxShadow:"0 2px 14px rgba(11,19,51,.06)",marginBottom:18}}>
              <h4 style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:4}}>Transparence des charges</h4>
              <p style={{fontSize:13,color:C.muted,marginBottom:18}}>Votre quote-part : <strong style={{color:C.blue}}>{Math.round(myShare * 100)}%</strong> ({resident.tantiemes} / {imm.total} tantièmes)</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={chartCharges} cx="50%" cy="50%" outerRadius={75} innerRadius={42} dataKey="value" paddingAngle={3}>
                    {chartCharges.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={v => fmt(v)} contentStyle={{borderRadius:10,border:"none"}} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{background:C.card,borderRadius:16,boxShadow:"0 2px 14px rgba(11,19,51,.06)",overflow:"hidden"}}>
              <table>
                <thead><tr><th>Description</th><th>Fournisseur</th><th>Total immeuble</th><th>Votre part</th></tr></thead>
                <tbody>
                  {charges.filter(c => c.immId === resident.immId).map(c => (
                    <tr key={c.id}>
                      <td style={{fontWeight:600}}>{c.description}</td>
                      <td style={{color:C.muted}}>{c.fournisseur}</td>
                      <td style={{fontWeight:700}}>{fmt(c.montant)}</td>
                      <td style={{fontWeight:700,color:C.blue}}>{fmt(c.montant * myShare)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "travaux" && (
          <div className="fi">
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
              {travaux.filter(t => t.immId === resident.immId).map((t, i) => (
                <div key={t.id} className="fu ch" style={{animationDelay:`${i*70}ms`,background:C.card,borderRadius:16,padding:20,boxShadow:"0 2px 14px rgba(11,19,51,.06)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                    <Tag s={t.statut} />
                    <span style={{fontSize:12,color:C.muted}}>{fmtD(t.date)}</span>
                  </div>
                  <h4 style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>{t.description}</h4>
                  <p style={{fontSize:12,color:C.muted,marginBottom:12}}>🏗 {t.fournisseur}</p>
                  <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden",marginBottom:5}}>
                    <div style={{height:"100%",background:t.avancement===100?C.green:C.teal,width:`${t.avancement}%`,borderRadius:3}} />
                  </div>
                  <p style={{fontSize:12,color:C.muted,textAlign:"right"}}>{t.avancement}%</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className="fi" style={{maxWidth:520}}>
            <div style={{background:C.card,borderRadius:16,padding:24,boxShadow:"0 2px 14px rgba(11,19,51,.06)"}}>
              <h4 style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:18}}>✉️ Contacter le syndic</h4>
              <Sel label="Objet" value="Question sur mes cotisations" onChange={() => {}}
                options={["Question sur mes cotisations","Signaler un problème","Demande de document","Réclamation","Autre"]} />
              <div style={{marginBottom:14}}>
                <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:".4px"}}>Message</label>
                <textarea className="inp" rows={5}
                  style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,color:C.text,resize:"vertical"}}
                  placeholder="Votre message..." />
              </div>
              <Btn onClick={() => alert("✅ Message envoyé ! Réponse sous 48h.")}>📤 Envoyer</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   PORTAIL PRESTATAIRE
══════════════════════════════════════ */
